import { Frame, RegularMasonryGrid } from "@masonry-grid/solid-js";
import { Alert } from "@ui/Alert";
import { Header } from "@ui/Header";
import { HStack, VStack } from "@ui/Stack";
import { UpdatedAt } from "@ui/UpdatedAt";
import { pluralise } from "@utils/text";
import type { Accessor } from "solid-js";
import {
	batch,
	createMemo,
	createResource,
	createSignal,
	For,
	Match,
	Show,
	Switch,
} from "solid-js";
import type {
	BoardItemClipVideoResponse,
	GetBoardClipData,
	ListBoardClipsResponses,
	VideoDetailResponse,
} from "@/client";
import {
	deleteClips,
	downloadAllAssets,
	downloadAssets,
	getBoard,
	getBoardClip,
	listBoardClips,
	restoreClips,
} from "@/client";
import { MoveBoardPanel } from "~/modules/gvtlabs/video/components/moveBoard/MoveBoardPanel";
import { AddToBoardProvider } from "~/modules/shared/boards/boardsContext";
import { Button } from "~/modules/shared/gvtui/Button";
import { toast } from "~/modules/shared/gvtui/molecules/Toasts/toast";
import {
	type BoardDetailsContextValue,
	BoardDetailsProvider,
	type ClipIds,
	useBoardDetails,
} from "./BoardDetailsContext";
import { BoardSettingsButton } from "./components/BoardSettingsButton";
import {
	CLIPS_MASONRY_FRAME_WIDTH,
	CLIPS_MASONRY_GAP,
	ClipsGrid,
} from "./components/ClipsGrid";
import { ClipsMenu } from "./components/ClipsMenu";
import { EmptyClipsList } from "./components/EmptyClipsList";
import { FollowBoardButton } from "./components/FollowBoardButton";
import { Title } from "./components/Title";
import "./boards-details.css";
import { Ghost } from "@ui/Ghost";
import { pageVars as layoutPageVars } from "@ui/layouts/AskConsumerLayout/pageVars";
import {
	type AppLayoutKind,
	useLayoutAppKind,
} from "@ui/layouts/layoutProvider";
import { MainContentContainer } from "@ui/layouts/MainContentContainer";
import { NoMatchingResults } from "@ui/molecules/NoMatchingResults/NoMatchingResults";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { triggerFileDownload } from "@utils/triggerFileDownload";
import { effect } from "solid-js/web";
import { mapBoardClipsToVideoEvidence } from "~/modules/askgvt/boards/mapBoardClipsToVideoEvidence";
import { ProfilePanel } from "~/modules/shared/chat/components/ProfilePanel";
import { useProfileMentionPanel } from "~/modules/shared/chat/components/useProfileMentionPanel";
import { useSearch } from "~/modules/shared/utils/usePagination";
import { ShareBoardButton } from "./components/ShareBoardButton";
import { PlayPanel } from "./components/videoPanel/PlayPanel";
import { getPublicBoard, listPublicBoardClips } from "./publicBoardApi";
import { useBoardClipsList } from "./useBoardClipsList";
import { useBoardPlayPanel } from "./useBoardPlayPanel";

export type { ClipIds } from "./BoardDetailsContext";

export type pageVars = {
	isInfluencer: boolean;
};

declare const pageVars: pageVars;

const backLinkMap: Record<AppLayoutKind, string> = {
	account: "/account",
	askgvt: "/boards",
	studio: "/studio/boards",
	admin: "/boards",
	labs: "/projects/:projectId/boards",
	roster: "/roster/boards",
	"agency-hub": "/roster/boards",
};

const startDownload = (blob: Blob) => {
	// The response is a Blob (ZIP file) from StreamedResponse
	if (!(blob instanceof Blob)) {
		toast.error({
			title: "Error downloading clips",
			description: "Invalid response format. Expected a ZIP file.",
		});
		return;
	}

	triggerFileDownload(
		blob,
		`clips_${new Date().toISOString().replace(/[:.]/g, "-")}.zip`,
	);
};

type Props = {
	projectId?: string;
	boardId: string;
	/** Public /@handle/boards/:id — same UI, no select / delete / download / remove. */
	readOnly?: boolean;
	backLink?: string;
	viewerIsOwner?: boolean;
};

function boardClipVideoToDetail(
	video: BoardItemClipVideoResponse | null | undefined,
	videoId?: string | null,
): VideoDetailResponse {
	return {
		id: video?.id ?? videoId ?? undefined,
		duration: video?.duration_seconds ?? undefined,
		post_title: video?.title ?? undefined,
		description: video?.description ?? undefined,
	};
}

export const BoardDetailsApp = (props: Props) => {
	const url = new URL(window.location.href);
	const initialPlayClipId = url.searchParams.get("clip_id");
	const [showDetails, setShowDetails] = createSignal(false);
	const playPanel = useBoardPlayPanel();
	const profilePanel = useProfileMentionPanel();
	const [selectedClipIds, setSelectedClipIds] = createSignal<ClipIds[]>(
		JSON.parse(url.searchParams.get("clip_ids") ?? "[]"),
	);
	const [hasRestoredPlayFromUrl, setHasRestoredPlayFromUrl] = createSignal(
		initialPlayClipId == null,
	);

	const queryArgs = (): Omit<GetBoardClipData, "url"> => ({
		path: {
			board_id: props.boardId,
			clip_id:
				selectedClipIds().length === 0 ? undefined : selectedClipIds()[0].id,
		},
		query: {
			num_frames: 50,
		},
	});

	const [clip] = createResource(queryArgs, async (args) => {
		if (selectedClipIds().length === 0 || selectedClipIds()[0].id === "") {
			return null;
		}
		const res = await getBoardClip(args);
		return res.data;
	});

	const [clipCount, setClipCount] = createSignal(0);
	const [movePanelOpen, setMovePanelOpen] = createSignal(
		url.searchParams.get("panel") === "move",
	);
	const [boardError, setBoardError] = createSignal<string | null>(null);

	let resetClipsScroll = () => {};

	const syncClipSearchToUrl = (searchValue: string) => {
		const newSearch = new URLSearchParams(window.location.search);
		if (searchValue) {
			newSearch.set("search", searchValue);
		} else {
			newSearch.delete("search");
		}
		const qs = newSearch.toString();
		window.history.replaceState(
			null,
			"",
			qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
		);
	};

	const { handleSearch, handleClear, search } = useSearch({
		defaultSearch: url.searchParams.get("search") ?? "",
		onUpdate: () => {
			syncClipSearchToUrl(search());
			resetClipsScroll();
		},
	});

	const [board, { mutate: mutateBoard }] = createResource(async () => {
		const res = props.readOnly
			? await getPublicBoard(props.boardId)
			: await getBoard({
					path: { board_id: props.boardId },
				});
		if (res.error) {
			setBoardError((res.error as { message: string }).message);
			return;
		}
		setClipCount(res.data.clip_count);
		return res.data;
	});

	const {
		clips,
		setEl,
		end,
		reset: resetClipsList,
		mutate,
		loading,
		error,
		restoreLastMutation,
	} = useBoardClipsList(
		() => props.boardId,
		search,
		props.readOnly ? listPublicBoardClips : listBoardClips,
	);

	resetClipsScroll = resetClipsList;

	effect(() => {
		const newSearch = new URLSearchParams(window.location.search);
		if (movePanelOpen()) {
			newSearch.set("panel", "move");
			newSearch.set("clip_ids", JSON.stringify(selectedClipIds()));
			newSearch.delete("clip_id");
		} else {
			newSearch.delete("panel");
			newSearch.delete("clip_ids");

			if (playPanel.isPlayPanelActive()) {
				const clipList = clips();
				const index = playPanel.videoIndex();
				const clipId =
					clipList && index !== undefined
						? clipList[index]?.id
						: playPanel.clipToPlay()?.id;
				if (clipId) {
					newSearch.set("clip_id", clipId);
				} else {
					newSearch.delete("clip_id");
				}
			} else if (initialPlayClipId && !hasRestoredPlayFromUrl()) {
				newSearch.set("clip_id", initialPlayClipId);
			} else {
				newSearch.delete("clip_id");
			}
		}
		const qs = newSearch.toString();
		const nextUrl = qs
			? `${window.location.pathname}?${qs}`
			: window.location.pathname;
		window.history.replaceState(null, "", nextUrl);
	});

	effect(() => {
		if (hasRestoredPlayFromUrl() || !initialPlayClipId || movePanelOpen()) {
			return;
		}
		const clipList = clips();
		if (!clipList) {
			return;
		}
		const index = clipList.findIndex((clip) => clip.id === initialPlayClipId);
		if (index >= 0) {
			playPanel.openPlayPanel(clipList[index], index);
			setHasRestoredPlayFromUrl(true);
			return;
		}
		if (end()) {
			setHasRestoredPlayFromUrl(true);
		}
	});

	const isOwner = createMemo(() => {
		if (props.viewerIsOwner != null) {
			return props.viewerIsOwner;
		}
		return board()?.is_owner ?? false;
	});
	const canManage = createMemo(() => !props.readOnly && isOwner());
	const [mode, setMode] = createSignal<"bulk_select" | "view">("view");

	const handleSetMode = (nextMode: "bulk_select" | "view") => {
		setMode(nextMode);
		if (nextMode === "bulk_select" && playPanel.isPlayPanelActive()) {
			playPanel.closePlayPanel({ immediate: true });
		}
	};

	const openMovePanelForClip = (clipId: string) => {
		setSelectedClipIds([{ id: clipId }]);
		setMovePanelOpen(true);
	};

	const clipAddSource = createMemo(() => {
		if (!clip()?.source_s3_key) {
			return null;
		}
		const start = clip().clipped_start ?? clip().start ?? 0;
		const end = clip().clipped_end ?? clip().end ?? 0;
		if (end <= start) return null;
		return {
			source_s3_key: clip()?.source_s3_key,
			source_duration: end,
			start: start - clip().start,
			end: end - clip().start,
		};
	});

	const addToBoardVideo = createMemo((): VideoDetailResponse => {
		const clipId = selectedClipIds()[0]?.id;
		const listClip = clips()?.find((c) => c.id === clipId);
		if (listClip) {
			return boardClipVideoToDetail(listClip.video, listClip.video_id);
		}
		const activeClip = clip();
		return {
			id: activeClip?.video_id ?? undefined,
			duration: activeClip?.duration ?? undefined,
		};
	});

	const removeClipsFromUi = (clipIds: string[]) => {
		const playingIndex = playPanel.videoIndex();
		const playingId =
			(playingIndex !== undefined ? clips()?.[playingIndex]?.id : undefined) ??
			playPanel.clipToPlay()?.id;
		const selectedIds = selectedClipIds().map((clip) => clip.id);

		batch(() => {
			if (playingId && clipIds.includes(playingId)) {
				playPanel.closePlayPanel({ immediate: true });
			}
			if (movePanelOpen() && selectedIds.some((id) => clipIds.includes(id))) {
				setMovePanelOpen(false);
				setSelectedClipIds([]);
			}
			mutate((prev) => prev?.filter((clip) => !clipIds.includes(clip.id)));
		});
	};

	const handleDeleteClips = async (clip_ids: ClipIds[]) => {
		const clipLabel = pluralise(clip_ids.length, "clip");
		try {
			const response = await deleteClips({
				path: { board_id: props.boardId },
				body: { clip_ids: clip_ids.map((clip) => clip.id) },
			});
			if (response.error) {
				toast.error({
					duration: 30000,
					title: `Error deleting ${clipLabel}`,
					description: (response.error as { message: string }).message,
				});
				return;
			}
			removeClipsFromUi(clip_ids.map((clip) => clip.id));
			setSelectedClipIds([]);
			setClipCount((prev) => prev - clip_ids.length);
			toast.success({
				duration: 30000,
				title: `${clipLabel} deleted`,
				description: ({ handleClose }) => (
					<>
						<Button
							variant="secondary"
							theme="monochrome"
							onClick={async () => {
								const response = await restoreClips({
									body: { clip_ids: clip_ids.map((clip) => clip.id) },
									path: { board_id: props.boardId },
								});
								if (response.error) {
									toast.error({
										duration: 30000,
										title: `Error restoring ${clipLabel}`,
										description: (response.error as { message: string })
											.message,
									});
									return;
								}
								setClipCount((prev) => prev + clip_ids.length);
								restoreLastMutation();
								handleClose();
								toast.info({
									title: `${clipLabel} restored`,
								});
							}}
						>
							Undo
						</Button>
					</>
				),
			});
		} catch (error) {
			console.error(error);
		}
	};

	const handleDownloadAllAssets = async () => {
		if (!pageVars.isInfluencer) {
			return null;
		}
		try {
			const response = await downloadAllAssets({
				path: { board_id: props.boardId },
			});
			startDownload(response.data);
			if (response.error) {
				toast.error({
					title: "Error downloading clips",
					description: (response.error as { message: string }).message,
				});
				return;
			}
			toast.success({
				title: "Download started",
				description: `Downloading all assets...`,
			});
		} catch (error) {
			console.error(error);
		}
	};

	const handleDownloadClips = async (clip_ids: ClipIds[]) => {
		if (!pageVars.isInfluencer) {
			return null;
		}
		if (clip_ids.length === 0) {
			toast.warning({
				title: "No clips selected",
				description: "Please select at least one clip to download.",
			});
			return;
		}
		try {
			const response = await downloadAssets({
				path: { board_id: props.boardId },
				body: { clip_ids: clip_ids.map((clip) => clip.id) },
				parseAs: "blob",
			});

			if (response.error) {
				toast.error({
					title: "Error downloading clips",
					description: (response.error as { message: string }).message,
				});
				return;
			}

			startDownload(response.data);

			toast.success({
				title: "Download started",
				description: `Downloading ${pluralise(clip_ids.length, "clip")}...`,
			});
		} catch (error) {
			console.error("Failed to download clips as ZIP:", error);
			toast.error({
				title: "Error downloading clips",
				description:
					error instanceof Error
						? error.message
						: "An unexpected error occurred while downloading the clips.",
			});
		}
	};
	const ghostCount = () => Math.max(1, clips()?.length ?? 0);
	const appKind = useLayoutAppKind();
	const backLink =
		props.backLink ??
		backLinkMap[appKind].replace(":projectId", props.projectId ?? "");
	const boardDetailsContextValue: BoardDetailsContextValue = {
		boardId: props.boardId,
		projectId: props.projectId,
		isOwner,
		canManage,
		clipCount,
		showDetails,
		setShowDetails,
		mode,
		setMode: handleSetMode,
		selectedClipIds,
		setSelectedClipIds,
		deleteClips: handleDeleteClips,
		downloadClips: handleDownloadClips,
		downloadAllAssets: handleDownloadAllAssets,
		showDownloadButton: pageVars.isInfluencer,
		clipSearch: search,
		searchProps: {
			get value() {
				return search();
			},
			onSearch: handleSearch,
			onClear: handleClear,
		},
		playVideo: playPanel.openPlayPanel,
		activePlayClipId: () => {
			if (!playPanel.isPlayPanelActive()) {
				return undefined;
			}
			const index = playPanel.videoIndex();
			const clipList = clips();
			if (index === undefined || clipList == null) {
				return undefined;
			}
			return clipList[index]?.id;
		},
		openMovePanel: (clipIds) => {
			setMovePanelOpen(true);
			setSelectedClipIds(clipIds);
		},
	};

	return (
		<BoardDetailsProvider value={boardDetailsContextValue}>
			<Header
				backLink={backLink}
				title={<Title board={board} />}
				class="boards-header"
				titleFooter={
					<HStack spacing="medium">
						<Show when={board()}>
							<UpdatedAt updatedAt={board()?.updated_at} />
						</Show>
					</HStack>
				}
			>
				<Show when={board()}>
					<HStack spacing="medium">
						<FollowBoardButton
							boardId={props.boardId}
							board={board}
							mutate={mutateBoard}
							isOwner={isOwner}
							canFollow={() => !props.readOnly || layoutPageVars().isSignedIn}
						/>
						<ShareBoardButton board={board()} />
						<BoardDetailsHeaderActions clips={clips} />
						<BoardSettingsButton
							board={board}
							mutate={mutateBoard}
							isOwner={canManage()}
						/>
					</HStack>
				</Show>
			</Header>
			<MainContentContainer class="boards-details-container">
				<HStack spacing="medium" class="boards-details">
					<VStack spacing="medium" class="boards-details__clips-pane">
						<Show when={!boardError() && board()}>
							<ClipsMenu />
						</Show>
						<Switch>
							<Match when={boardError()}>
								<Alert variant="error">Error: {boardError()}</Alert>
							</Match>
							<Match when={loading()}>
								<div class="boards-details__clips-scroll">
									<RegularMasonryGrid
										class="clips-grid"
										frameWidth={CLIPS_MASONRY_FRAME_WIDTH}
										gap={CLIPS_MASONRY_GAP}
									>
										<For each={Array(ghostCount())}>
											{() => (
												<Frame width={9} height={16}>
													<Ghost
														height="100%"
														width="100%"
														style={{ "border-radius": "0.5rem" }}
													/>
												</Frame>
											)}
										</For>
									</RegularMasonryGrid>
								</div>
							</Match>
							<Match when={error()}>
								<div>Error: {error()}</div>
							</Match>
							<Match when={clips() && clips().length > 0}>
								<Show
									when={!props.readOnly}
									fallback={<ClipsGrid clips={clips} />}
								>
									<AddToBoardProvider
										projectId={props.projectId}
										onUpdateBoardClip={(payload) => {
											const {
												sourceBoardId,
												targetBoardId,
												clipId,
												moveClipTrim,
											} = payload;
											if (!clipId) {
												return;
											}
											const movedToAnotherBoard =
												targetBoardId != null &&
												sourceBoardId != null &&
												targetBoardId !== sourceBoardId;
											if (movedToAnotherBoard) {
												removeClipsFromUi([clipId]);
												return;
											}
											mutate((prev) =>
												prev?.map((clip) =>
													clip.id === clipId
														? {
																...clip,
																clipped_start: moveClipTrim.start,
																clipped_end: moveClipTrim.end,
															}
														: clip,
												),
											);
										}}
										board={board()}
										clip={clip()}
										isLoading={() => clip.loading}
										video={addToBoardVideo()}
										clipAddSource={clipAddSource}
									>
										<ClipsGrid clips={clips} />

										<MoveBoardPanel
											isOpen={movePanelOpen}
											setIsOpen={setMovePanelOpen}
											projectId={props.projectId}
											refetch={(clipIds: string[]) => {
												removeClipsFromUi(clipIds);
												setClipCount((prev) => prev - clipIds.length);
											}}
										/>
									</AddToBoardProvider>
								</Show>

								<Show when={!end()}>
									<h3 ref={setEl}>Loading more ...</h3>
								</Show>
							</Match>
							<Match
								when={clips() && clips().length === 0 && search().trim() !== ""}
							>
								<NoMatchingResults
									message="No clips match your search"
									hint="Try a different video title"
								/>
							</Match>
							<Match when={clips() && clips().length === 0}>
								<Show
									when={!props.readOnly}
									fallback={<p>No clips to show yet.</p>}
								>
									<EmptyClipsList isOwner={isOwner} />
								</Show>
							</Match>
						</Switch>
					</VStack>
					<PlayPanel
						boardId={props.boardId}
						isMounted={playPanel.playPanelMounted}
						isOpen={playPanel.playPanelOpen}
						isClosing={playPanel.playPanelClosing}
						layoutAnimating={playPanel.playPanelLayoutAnimating}
						onToggle={playPanel.togglePlayPanel}
						onEditClip={canManage() ? openMovePanelForClip : undefined}
						onVideoIndexChange={playPanel.setActiveVideoIndex}
						clip={playPanel.clipToPlay()}
						videoIndex={playPanel.videoIndex}
						carouselInstantJumpToken={playPanel.carouselInstantJumpToken}
						videoEvidence={mapBoardClipsToVideoEvidence(clips()) ?? []}
					/>
					<ProfilePanel
						isOpen={profilePanel.isOpen}
						mention={profilePanel.profileMention}
						onClose={profilePanel.close}
					/>
				</HStack>
			</MainContentContainer>
		</BoardDetailsProvider>
	);
};

const BoardDetailsHeaderActions = (props: {
	clips: Accessor<ListBoardClipsResponses[200] | undefined>;
}) => {
	const boardDetails = useBoardDetails();

	return (
		<Show
			when={
				props.clips() && props.clips().length > 0 && boardDetails.canManage()
			}
		>
			<Show when={boardDetails.showDownloadButton}>
				<TooltipTrigger>
					<Button
						aria-label="Download board"
						variant="tertiary"
						style={{ width: "2rem", height: "2rem" }}
						symbol="download"
						onClick={() => {
							void boardDetails.downloadAllAssets();
						}}
					></Button>
					<Tooltip>Download board</Tooltip>
				</TooltipTrigger>
			</Show>
		</Show>
	);
};
